import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useIdentityDocumentVerificationsQuery } from "Api/Queries/Party/useIdentityDocumentVerificationsQuery";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { GridItem } from "Components/Shared/GridItem";
import { VerificationValue } from "Components/Users/UserDetailPageContent/Components/UserReadOnlyDetail/VerificationValue";
import type { VerificationStatus } from "Components/Users/UserDetailPageContent/Components/UserReadOnlyDetail/verificationStatusUtils";
import {
  IdentityDocumentSlot,
  IdentityDocumentVerdict,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";

const RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Party.Form
    .PartyIdentification.AutomatedCheck;

const verdictStatus: Record<IdentityDocumentVerdict, VerificationStatus> = {
  [IdentityDocumentVerdict.NotChecked]: "pending",
  [IdentityDocumentVerdict.Approved]: "verified",
  [IdentityDocumentVerdict.Declined]: "not-verified",
  [IdentityDocumentVerdict.InReview]: "pending",
  [IdentityDocumentVerdict.Unavailable]: "pending",
};

type Props = {
  partyPublicID: string;
};

/** History of the automated checks (Didit) of the party's documents, newest first. */
export const AutomatedDocumentCheckSection = ({ partyPublicID }: Props) => {
  const { t } = useResource();
  const {
    data: checks = [],
    isLoading,
    isError,
  } = useIdentityDocumentVerificationsQuery(partyPublicID);

  if (isLoading) {
    return <CenteredCircularProgress />;
  }

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} mb={1}>
        {t(RESOURCES.Title)}
      </Typography>
      {isError && (
        <Typography color="error">{t(RESOURCES.LoadingError)}</Typography>
      )}
      {!isError && checks.length === 0 && (
        <Typography color="text.secondary">{t(RESOURCES.NoChecks)}</Typography>
      )}
      <Stack gap={2}>
        {checks.map((check) => (
          <Box key={check.identityDocumentVerificationID}>
            <Grid container spacing={2}>
              <GridItem
                label={t(RESOURCES.Document)}
                value={t(
                  check.document === IdentityDocumentSlot.Secondary
                    ? RESOURCES.Documents.Secondary
                    : RESOURCES.Documents.Primary,
                )}
              />
              <GridItem
                label={t(RESOURCES.Verdict)}
                value={
                  <VerificationValue
                    status={verdictStatus[check.verdict]}
                    label={t(
                      Resources.CodeLists.IdentityDocumentVerdict[
                        check.verdict
                      ],
                    )}
                  />
                }
              />
              <GridItem
                label={t(RESOURCES.Date)}
                value={formatDate(check.verifiedAt, true)}
              />
              <GridItem
                label={t(RESOURCES.DocumentType)}
                value={check.documentType}
              />
              <GridItem
                label={t(RESOURCES.RequestID)}
                value={check.externalRequestID}
              />
              {check.errorMessage && (
                <GridItem
                  label={t(RESOURCES.Error)}
                  value={check.errorMessage}
                />
              )}
            </Grid>
            {check.warnings.length > 0 && (
              <Box mt={1}>
                <Typography variant="body2" color="text.secondary">
                  {t(RESOURCES.Warnings)}
                </Typography>
                <List dense disablePadding>
                  {check.warnings.map((warning, index) => (
                    <ListItem
                      key={`${warning.code}-${index}`}
                      disableGutters
                      sx={{ py: 0 }}
                    >
                      <ListItemText
                        primary={warning.shortDescription || warning.code}
                        secondary={
                          warning.shortDescription ? warning.code : undefined
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
